import {
  Badge,
  Button,
  Center,
  Divider,
  Group,
  Skeleton,
  Stack,
  Table,
  Text,
  ThemeIcon,
  Tooltip,
} from '@mantine/core';
import { IconAlertTriangle, IconCheck, IconMoodCheck } from '@tabler/icons-react';
import { useEffect, useState } from 'react';

import type { RefreshInterval } from '../api/queries';
import { useDebts } from '../api/queries';
import type { Debt } from '../api/types';
import { formatDate, formatMoney, today } from '../lib/format';
import { DebtDetails } from './DebtDetails';

/** The whole list is shown at once; beyond this it is cut off with a hint. */
export const DEBTS_LIMIT = 50;

const SKELETON_ROWS = 3;

/**
 * Debts and what is left to pay on each. Read-only like the rest of the UI: a
 * debt and its payment schedule are written through MCP.
 *
 * There is no stored amount anywhere — «осталось» is the schedule of the debt
 * added up, so it is only ever as right as the schedule is.
 */
export function DebtsPanel({
  refreshInterval,
  onCountChange,
}: {
  refreshInterval: RefreshInterval;
  onCountChange?: (count: number | null) => void;
}) {
  const { data, isPending, error, refetch } = useDebts(DEBTS_LIMIT, refreshInterval);
  const [openedId, setOpenedId] = useState<string | null>(null);

  const debts = data?.debts ?? [];

  useEffect(() => {
    onCountChange?.(error !== null || isPending ? null : debts.length);
  }, [debts.length, error, isPending, onCountChange]);

  return (
    <>
      <Stack gap="md">
        {error ? (
          <ErrorState
            message={error.message}
            onRetry={() => {
              void refetch();
            }}
          />
        ) : isPending ? (
          <Stack gap="sm">
            {Array.from({ length: SKELETON_ROWS }, (_, index) => (
              <Skeleton key={index} height={44} radius="sm" />
            ))}
          </Stack>
        ) : debts.length === 0 ? (
          <EmptyState />
        ) : (
          <DebtsTable debts={debts} onSelect={setOpenedId} />
        )}

        {!error && !isPending && debts.length > 0 && (
          <DebtsTotal debts={debts} truncated={data?.hasMore ?? false} />
        )}
      </Stack>

      <DebtDetails debtId={openedId} onClose={() => setOpenedId(null)} />
    </>
  );
}

function DebtsTable({ debts, onSelect }: { debts: Debt[]; onSelect: (id: string) => void }) {
  return (
    <Table.ScrollContainer minWidth={320}>
      <Table highlightOnHover verticalSpacing="xs" horizontalSpacing="xs">
        <Table.Thead>
          <Table.Tr>
            <Table.Th>
              <Text size="xs" c="dimmed" fw={600} tt="uppercase">
                Долг
              </Text>
            </Table.Th>
            <Table.Th ta="right">
              <Text size="xs" c="dimmed" fw={600} tt="uppercase">
                Осталось
              </Text>
            </Table.Th>
          </Table.Tr>
        </Table.Thead>

        <Table.Tbody>
          {debts.map((debt) => (
            <DebtRow key={debt.id} debt={debt} onSelect={onSelect} />
          ))}
        </Table.Tbody>
      </Table>
    </Table.ScrollContainer>
  );
}

function DebtRow({ debt, onSelect }: { debt: Debt; onSelect: (id: string) => void }) {
  return (
    <Table.Tr
      style={{ cursor: 'pointer' }}
      onClick={() => onSelect(debt.id)}
      tabIndex={0}
      onKeyDown={(event) => {
        if (event.key === 'Enter') {
          onSelect(debt.id);
        }
      }}
    >
      <Table.Td>
        <Stack gap={2} style={{ minWidth: 0 }}>
          <Group gap={6} wrap="nowrap">
            <Text size="sm" fw={500} lineClamp={1}>
              {debt.lender}
            </Text>
            <RevolvingBadge debt={debt} />
          </Group>
          <Text size="xs" c="dimmed" lineClamp={1}>
            {debt.borrower}
          </Text>
        </Stack>
      </Table.Td>

      <Table.Td ta="right">
        <Stack gap={2} align="flex-end" style={{ whiteSpace: 'nowrap' }}>
          <Text size="sm" fw={600} style={{ fontVariantNumeric: 'tabular-nums' }}>
            {formatMoney(debt.remaining)}
          </Text>
          <NextPayment debt={debt} />
        </Stack>
      </Table.Td>
    </Table.Tr>
  );
}

/**
 * A rolled debt is a different kind of number: the schedule says what has to be
 * on the account that day, not what the month loses.
 */
function RevolvingBadge({ debt }: { debt: Debt }) {
  if (!debt.revolving) {
    return null;
  }

  return (
    <Tooltip
      label="Гасится целиком и выбирается снова: деньги нужны в день платежа, но возвращаются"
      withArrow
    >
      <Badge color="grape" variant="light" size="xs" style={{ flexShrink: 0 }}>
        револьверный
      </Badge>
    </Tooltip>
  );
}

/** An empty schedule is the only way a debt says it is paid off. */
function NextPayment({ debt }: { debt: Debt }) {
  if (debt.next_due_on === null) {
    return (
      <Badge color="teal" size="xs" leftSection={<IconCheck size={11} stroke={2} />}>
        закрыт
      </Badge>
    );
  }

  const overdue = debt.next_due_on < today();

  return (
    <Tooltip label={`Платежей осталось: ${debt.payments_left}`} withArrow>
      <Text size="xs" c={overdue ? 'red' : 'dimmed'}>
        {overdue ? 'просрочен ' : 'до '}
        {formatDate(debt.next_due_on)}
      </Text>
    </Tooltip>
  );
}

function DebtsTotal({ debts, truncated }: { debts: Debt[]; truncated: boolean }) {
  const total = debts.reduce((sum, debt) => sum + debt.remaining, 0);

  return (
    <>
      <Divider />
      <Group justify="space-between">
        <Text size="xs" c="dimmed">
          {truncated ? `Первые ${debts.length} из списка` : 'Итого осталось'}
        </Text>
        <Text size="sm" fw={600} style={{ fontVariantNumeric: 'tabular-nums' }}>
          {formatMoney(total)}
        </Text>
      </Group>
    </>
  );
}

function ErrorState({ message, onRetry }: { message: string; onRetry: () => void }) {
  return (
    <Center py={24}>
      <Stack gap="xs" align="center" maw={280}>
        <ThemeIcon variant="light" color="red" size="lg" radius="md">
          <IconAlertTriangle size={18} stroke={1.5} />
        </ThemeIcon>
        <Text size="sm" fw={600}>
          Не удалось загрузить долги
        </Text>
        <Text size="xs" c="dimmed" ta="center">
          {message}
        </Text>
        <Button size="xs" variant="default" mt={4} onClick={onRetry}>
          Повторить
        </Button>
      </Stack>
    </Center>
  );
}

function EmptyState() {
  return (
    <Center py={32}>
      <Stack gap="xs" align="center" maw={240}>
        <IconMoodCheck size={26} stroke={1.5} opacity={0.5} />
        <Text size="sm" c="dimmed" ta="center">
          Долгов нет
        </Text>
        <Text size="xs" c="dimmed" ta="center">
          Завести долг и его график платежей можно через MCP
        </Text>
      </Stack>
    </Center>
  );
}
